import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ShieldCheck, Star, Clock } from "lucide-react";

export default function HomeNursingBanner({ language, waLink, doctors = [], onBook }) {
  const isSq = language === "sq";

  const nursingSlides = useMemo(() => [
    {
      key: "inj", type: "nursing",
      img: "/banners/home-nursing-1.jpg",
      kicker: isSq ? "Shërbim në shtëpi" : "At-home service",
      title: isSq ? "Infermier/e • Fizioterapi" : "Nursing • Physiotherapy",
      sub: isSq ? "Terapi, grafi në shtëpi, fizioterapi profesionale." : "Therapy, home X-ray, professional physiotherapy.",
      msg: isSq ? "Përshëndetje! Dua shërbim në shtëpi (infermier/fizioterapi)." : "Hi! I need at-home nursing/physiotherapy.",
    },
    {
      key: "ambulance", type: "nursing",
      img: "/banners/home-nursing-3.jpg",
      kicker: isSq ? "Shërbim ambulance" : "Ambulance service",
      title: isSq ? "Brenda dhe jashtë Shqipërisë" : "Domestic & international",
      sub: isSq ? "Transport mjekësor i sigurt 24/7, staf i specializuar." : "Safe medical transport 24/7 with specialized staff.",
      msg: isSq ? "Përshëndetje! Dua shërbim ambulance." : "Hi! I need an ambulance service.",
    },
  ], [isSq]);

  // Only doctors with real http photos
 const doctorSlides = useMemo(() => {
  const withPhotos = doctors.filter((d) => d.photoURL?.startsWith("http"));
  
  // Fisher-Yates shuffle
  const shuffled = [...withPhotos];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled.slice(0, 5).map((d) => ({ key: `doc-${d.id}`, type: "doctor", doctor: d }));
}, [doctors.length]); // only re-shuffle when doctor count changes, not on every render

  // Interleave: nursing[0], doctor[0], nursing[1], doctor[1]...
  const slides = useMemo(() => {
    const out = [];
    const max = Math.max(nursingSlides.length, doctorSlides.length);
    for (let i = 0; i < max; i++) {
      if (nursingSlides[i]) out.push(nursingSlides[i]);
      if (doctorSlides[i]) out.push(doctorSlides[i]);
    }
    return out.length > 0 ? out : nursingSlides;
  }, [nursingSlides, doctorSlides]);

  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });

  const stopAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startAuto = useCallback(() => {
    stopAuto();
    timerRef.current = setInterval(() => {
      setIdx((v) => (v + 1) % slides.length);
    }, 4500);
  }, [slides.length, stopAuto]);

  useEffect(() => { startAuto(); return stopAuto; }, [startAuto, stopAuto]);

  const go = (next) => setIdx((next + slides.length) % slides.length);
  const onDot = (i) => { stopAuto(); setIdx(i); startAuto(); };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
    stopAuto();
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? go(idx + 1) : go(idx - 1);
    }
    startAuto();
  };

  const slide = slides[idx];

  return (
    <div className="mdk-hn">
      <div className="mdk-hn__viewport" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="mdk-hn__track" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {slides.map((s) => (
            <div className="mdk-hn__slide" key={s.key}>

              {/* NURSING SLIDE */}
              {s.type === "nursing" && (
                <>
                  <img className="mdk-hn__img" src={s.img} alt={s.title} />
                  <div className="mdk-hn__overlay" />
                  <div className="mdk-hn__content">
                    <div className="mdk-hn__kicker">{s.kicker}</div>
                    <div className="mdk-hn__title">{s.title}</div>
                    <div className="mdk-hn__sub">{s.sub}</div>
                    <div className="mdk-hn__actions">
                      <a className="mdk-hn__cta" href={waLink(s.msg)} target="_blank" rel="noreferrer">
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </>
              )}

              {/* DOCTOR SLIDE */}
              {/* DOCTOR SLIDE */}
{s.type === "doctor" && (() => {
  // Deterministic random based on doctor id
  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)",
    "linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)",
    "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)",
    "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
    "linear-gradient(135deg, #f6d365 0%, #fda085 100%)",
  ];

  // Pick based on doctor id so it's stable across re-renders
  const hash = (s.doctor.id || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const bg = gradients[hash % gradients.length];

  // Decide text color based on whether gradient is light or dark
  const lightGradients = [2, 3, 6, 7, 8, 9, 11]; // indices with light colors
  const isLight = lightGradients.includes(hash % gradients.length);
  const textColor = isLight ? "rgba(11,15,24,.85)" : "rgba(255,255,255,.95)";
  const subColor = isLight ? "rgba(11,15,24,.6)" : "rgba(255,255,255,.75)";
  const badgeBg = isLight ? "rgba(0,0,0,.12)" : "rgba(255,255,255,.2)";
  const badgeColor = isLight ? "rgba(11,15,24,.75)" : "#fff";

  return (
    <div style={{
      display: "flex", alignItems: "center", height: "100%",
      padding: "0 20px", gap: 16,
      background: bg,
      position: "relative", overflow: "hidden",
    }}>
      {/* Decorative blur blob */}
      <div style={{
        position: "absolute", width: 160, height: 160,
        borderRadius: "50%", right: -40, top: -40,
        background: "rgba(255,255,255,.12)",
        filter: "blur(40px)", pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", width: 100, height: 100,
        borderRadius: "50%", right: 40, bottom: -30,
        background: "rgba(255,255,255,.08)",
        filter: "blur(24px)", pointerEvents: "none",
      }} />

      {/* Photo */}
      <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
        <img
          src={s.doctor.photoURL}
          alt={s.doctor.name}
          style={{
            width: 84, height: 84, borderRadius: 18,
            objectFit: "cover",
            border: "3px solid rgba(255,255,255,.4)",
            boxShadow: "0 8px 24px rgba(0,0,0,.2)",
          }}
        />
        {s.doctor.verified && (
          <div style={{
            position: "absolute", bottom: -4, right: -4,
            background: "#fff", borderRadius: "50%",
            width: 22, height: 22,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid rgba(99,102,241,.3)",
          }}>
            <ShieldCheck size={11} color="rgb(99,102,241)" />
          </div>
        )}
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
        <div style={{
          fontSize: 15, fontWeight: 900,
          color: textColor, marginBottom: 2,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {s.doctor.name}
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: subColor, marginBottom: 8 }}>
          {s.doctor.specialtyLabel || s.doctor.specialtyKey}
          {s.doctor.city ? ` • ${s.doctor.city}` : ""}
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 12, fontWeight: 700, color: badgeColor,
            background: badgeBg, padding: "2px 8px", borderRadius: 20,
          }}>
            <Star size={11} color="#f59e0b" fill="#f59e0b" />
            {s.doctor.rating || "4.5"}
          </span>

          {s.doctor.feeText && (
            <span style={{
              fontSize: 12, fontWeight: 900,
              color: badgeColor, background: badgeBg,
              padding: "2px 9px", borderRadius: 20,
            }}>
              💊 {s.doctor.feeText}
            </span>
          )}

          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            fontSize: 11, fontWeight: 700,
            color: badgeColor, background: badgeBg,
            padding: "2px 8px", borderRadius: 20,
          }}>
            <Clock size={10} />
            {isSq ? "Disponibël" : "Available"}
          </span>
        </div>

       <button
  type="button"
  onClick={(e) => {
    e.stopPropagation();
    if (onBook) onBook(s.doctor);
  }}
  style={{
    background: isLight ? "rgba(11,15,24,.75)" : "rgba(255,255,255,.95)",
    color: isLight ? "#fff" : "rgba(11,15,24,.85)",
    border: "none", padding: "7px 18px", borderRadius: 20,
    fontSize: 12, fontWeight: 900, cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,.15)",
  }}
>
  {isSq ? "Rezervo vizitën →" : "Book visit →"}
</button>
      </div>
    </div>
  );
})()}

            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="mdk-hn__dots" role="tablist" aria-label="Slides">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`mdk-hn__dot ${i === idx ? "is-active" : ""}`}
              onClick={() => onDot(i)}
              aria-label={`Slide ${i + 1}`}
              type="button"
            />
          ))}
        </div>
      </div>
    </div>
  );
}